/**
 * OptionPopup component displays a form to edit a set of additionnal options.
 * This component handles user inputs for bucket and actuarial rate settings.
 */

import React, { useState, useEffect, useRef } from "react";
import { Form, SimpleItem, GroupItem, Label } from "devextreme-react/form";
import PopupWrapper from "./PopupWrapper";
import {
  OSIRIS_BUCKET_RULE,
  OSIRIS_ACTUARIAL_RATE_RULE,
} from "data/ENUM/add-options";
/**
 * Props:
 * - isVisible: Boolean to control visibility of the popup
 * - onClose: Callback function to handle popup close
 * - onCancel: Callback function to handle cancel action
 * - onConfirm: Callback function to handle form confirmation
 * - data: Initial data to populate the form
 * - title: Title of the popup
 * - prefix: Optional prefix for field names (e.g., "Departure", "Arrival")
 */
const OptionPopupOsiris = ({
  isVisible,
  onCancel,
  onConfirm,
  title,
  data,
  prefix = "",
}) => {
  /* State variables */
  const [localData, setLocalData] = useState({});
  const [isReadOnly, setIsReadOnly] = useState(false);
  const formRef = useRef(null);

  // Add prefix to field names if it exists
  const prefixField = (field) => (prefix ? `${prefix}${field}` : field);

  // Unprefix the field names for local display
  const unprefixField = (field) => {
    if (!prefix) return field;
    return field.includes(prefix) ? field.substring(prefix.length) : field;
  };

  // Convert the prefixed data to non-prefixed local data
  const convertToLocalData = (data) => {
    if (!data) return {};

    const result = {
      Bucket: false,
      bucketRules: "",
      ActuarialRate: false,
      actuarialRateRules: "",
    };

    Object.keys(data).forEach((key) => {
      if (prefix && key.startsWith(prefix)) {
        const unprefixedKey = unprefixField(key);
        result[unprefixedKey] = data[key];
      } else if (!prefix) {
        if (
          [
            "Bucket",
            "bucketRules",
            "ActuarialRate",
            "actuarialRateRules",
          ].includes(key)
        ) {
          result[key] = data[key];
        }
      }
    });

    if (prefix) {
      result.Bucket = data[`${prefix}Bucket`] || false;
      result.bucketRules = data[`${prefix}bucketRules`] || "";
      result.ActuarialRate = data[`${prefix}ActuarialRate`] || false;
      result.actuarialRateRules = data[`${prefix}actuarialRateRules`] || "";
    }

    return result;
  };

  // Convert the local data to prefixed data for the parent
  const convertToParentData = (localData) => {
    const result = {};

    Object.keys(localData).forEach((key) => {
      result[prefixField(key)] = localData[key];
    });

    return result;
  };

  /**
   * Updates the local data when the data changes in the parent.
   */
  useEffect(() => {
    if (isVisible) {
      setIsReadOnly(Boolean(data.id));
      setLocalData(convertToLocalData(data));

      // Ensure validation group is properly initialized when form becomes visible
      if (formRef.current && formRef.current.instance) {
        setTimeout(() => {
          try {
            const formInstance = formRef.current.instance();
            if (formInstance && !formInstance.option("validationGroup")) {
              formInstance.option("validationGroup", "optionPopupValidation");
            }
          } catch (error) {
            console.warn("Error initializing validation group:", error);
          }
        }, 0);
      }
    }
  }, [isVisible, data]);

  /**
   * Handles the cancel button click. Resets the local data and triggers the onCancel callback.
   */
  const handleCancel = () => {
    if (isReadOnly) {
      onCancel({ keepData: true });
    } else {
      setLocalData({});
      onCancel({ keepData: false });
    }
  };

  /**
   * Handles the confirm button click. Verifies the form data and confirms the changes.
   */
  const handleConfirm = () => {
    if (!formRef.current || !formRef.current.instance) {
      console.warn("Form instance not available");
      return;
    }

    try {
      const formInstance = formRef.current.instance();

      // Ensure validation group is set before validating
      const currentValidationGroup = formInstance.option("validationGroup");
      if (!currentValidationGroup) {
        formInstance.option("validationGroup", "optionPopupValidation");
      }

      const validationResult = formInstance.validate();
      if (validationResult.isValid) {
        const parentData = convertToParentData(localData);
        onConfirm(parentData);
      } else {
        console.log("Invalid fields:", validationResult.brokenRules);
      }
    } catch (error) {
      console.error("Error during form validation:", error);
      if (error.message && error.message.includes("_updateBrokenRules")) {
        const parentData = convertToParentData(localData);
        onConfirm(parentData);
      } else {
        throw error;
      }
    }
  };

  /**
   * Handles BucketCheckbox changes: reset linked items if the box is unchecked
   */
  const handleBucketCheckboxChange = (e) => {
    const newFormData = {
      ...localData,
      Bucket: e.value,
    };
    if (!e.value) {
      newFormData.bucketRules = "";
    }
    setLocalData(newFormData);

    if (formRef.current && formRef.current.instance) {
      try {
        const formInstance = formRef.current.instance();
        formInstance.getEditor("bucketRules")?.reset();
      } catch (error) {
        console.warn("Form instance not available:", error);
      }
    }
  };

  /**
   * Handles ActuarialRateCheckbox changes: reset linked items if the box is unchecked
   */
  const handleActuarialRateCheckboxChange = (e) => {
    const newFormData = {
      ...localData,
      ActuarialRate: e.value,
    };
    if (!e.value) {
      newFormData.actuarialRateRules = "";
    }
    setLocalData(newFormData);

    if (formRef.current && formRef.current.instance) {
      try {
        const formInstance = formRef.current.instance();
        formInstance.getEditor("actuarialRateRules")?.reset();
      } catch (error) {
        console.warn("Form instance not available:", error);
      }
    }
  };

  return (
    <PopupWrapper
      title={title ? `Options ${title}` : "Options"}
      icon="edit"
      isVisible={isVisible}
      width="100vh"
      height="auto"
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    >
      <Form
        formData={localData}
        colCount={2}
        ref={formRef}
        validationGroup="optionPopupValidation"
      >
        {/* Group for "Bucket" settings */}
        <GroupItem caption="Bucket">
          <SimpleItem
            dataField="Bucket"
            editorType="dxCheckBox"
            editorOptions={{
              readOnly: isReadOnly,
              onValueChanged: handleBucketCheckboxChange,
            }}
          >
            <Label text="Bucket" />
          </SimpleItem>

          <SimpleItem
            dataField="bucketRules"
            editorType="dxSelectBox"
            editorOptions={{
              placeholder: "Select...",
              items: Object.values(OSIRIS_BUCKET_RULE),
              displayExpr: "label",
              valueExpr: "id",
              readOnly: isReadOnly,
              disabled: !localData.Bucket,
            }}
            validationRules={[
              {
                type: "custom",
                message: "Bucket rules are required if Bucket is checked.",
                validationCallback: ({ value, validator }) => {
                  const formData =
                    validator?.component?.option("formData") || localData;
                  return (
                    !formData?.Bucket ||
                    (value !== null && value !== undefined && value !== "")
                  );
                },
              },
            ]}
          >
            <Label text="Rules to apply" />
          </SimpleItem>
        </GroupItem>

        {/* Group for "Actuarial" settings */}
        <GroupItem caption="Actuarial">
          <SimpleItem
            dataField="ActuarialRate"
            editorType="dxCheckBox"
            editorOptions={{
              readOnly: isReadOnly,
              onValueChanged: handleActuarialRateCheckboxChange,
            }}
          >
            <Label text="Actuarial Rate" />
          </SimpleItem>

          <SimpleItem
            dataField="actuarialRateRules"
            editorType="dxSelectBox"
            editorOptions={{
              placeholder: "Select...",
              items: Object.values(OSIRIS_ACTUARIAL_RATE_RULE),
              displayExpr: "label",
              valueExpr: "id",
              readOnly: isReadOnly,
              disabled: !localData.ActuarialRate,
            }}
            validationRules={[
              {
                type: "custom",
                message:
                  "Actuarial rules are required if Actuarial Rate is checked.",
                validationCallback: ({ value, validator }) => {
                  // Get current form data to check ActuarialRate checkbox state
                  const formData =
                    validator?.component?.option("formData") || localData;
                  // Only validate if ActuarialRate is checked
                  return (
                    !formData?.ActuarialRate ||
                    (value !== null && value !== undefined && value !== "")
                  );
                },
              },
            ]}
          >
            <Label text="Rules to apply" />
          </SimpleItem>
        </GroupItem>
      </Form>
    </PopupWrapper>
  );
};

export default OptionPopupOsiris;
